import React from "react";
import PageWrapper from "components/PageWrapper";
import { Box, Stack, Typography, Pagination } from "@mui/material";
import Filter, { FilterValue, SEARCH_STATUS_KEYS } from "./Filter";
import Header from "components/Header";
import ReviewTable from "./ReviewTable";
import { AmlReviewStatistic, TPagination } from "../model";
import Statistic from "./Statistic";
import { AmlAPI } from "../api";
import { InvestorAmlOverview, SubCustomer } from "../api/transformers/aml";
import { useSnackbar } from "notistack";
import useBoolean from "../hooks/useBoolean";
import { useNavigate } from "react-router";
import { persistParams, restorePayloadFromParams } from "../utils/params";
import { GetReviewListParams } from "../api/aml";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "app/authHooks";

const PAGE_SIZE = 10;

function AmlReviewList() {
  const navigate = useNavigate();
  const { attributes } = useAuth();
  const [searchParams] = useSearchParams();
  const [initialFilter, initialPagination] = restorePayloadFromParams(
    searchParams,
    [paramsToFilter, paramsToPagination] as const
  );
  const [filter, setFilter] = React.useState<FilterValue>(initialFilter);
  const [pagination, setPagination] = React.useState<
    TPagination & { total: number }
  >({
    total: 0,
    ...initialPagination,
  });
  const [statistic, setStatistic] = React.useState<AmlReviewStatistic>({
    SUB: 0,
    REF: 0,
    EVI: 0,
    AUT: 0,
    NRQ: 0,
    ACT: 0,
    REJ: 0,
    PEPR: 0,
    SANR: 0,
    IDVR: 0,
    HRAF: 0,
  });
  const [isLoading, { setTrue: startLoading, setFalse: stopLoading }] =
    useBoolean(false);
  const [dataSource, setDataSource] = React.useState<InvestorAmlOverview[]>([]);
  const [showCustomerFilter, setShowCustomerFilter] = React.useState<boolean>(false);
  const [subCustomers, setSubCustomers] = React.useState<SubCustomer[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  React.useEffect(() => {
    fetchData(filter, pagination);
  }, []);

  const fetchData = async (filter: FilterValue, pagination: TPagination) => {
    startLoading();
    if (subCustomers.length === 0) {
      await fetchSubCustomers();
    }
    await fetchAmlReviewList(filter, pagination);
    stopLoading();
  };

  const fetchSubCustomers = () => {
    return AmlAPI.getSubCustomers()
      .then((response) => {
        setShowCustomerFilter(response.showCustomerFilter);
        setSubCustomers(response.customers);
      })
      .catch(() => {
        enqueueSnackbar("Failed to get sub customers.", { variant: "error" });
      });
  };

  const fetchAmlReviewList = (f: FilterValue, p: TPagination) => {
    setFilter(f);
    setPagination((pre) => ({
      total: pre.total,
      page: p.page,
      pageSize: p.pageSize,
    }));
    persistParams(navigate, {
      ...f,
      page: p.page,
      pageSize: p.pageSize,
    });
    return AmlAPI.getReviewList(getPayload(f, p))
      .then((response) => {
        setDataSource(response.rows);
        setStatistic(response.counts);
        setPagination((pre) => ({
          total: response.total,
          page: pre.page,
          pageSize: pre.pageSize,
        }));
      })
      .catch(() => {
        enqueueSnackbar("Failed to get AML Review List.", { variant: "error" });
      });
  };

  const handleFilterChange = (f: FilterValue, triggerFetch = false) => {
    if (triggerFetch) {
      fetchData(f, { page: 1, pageSize: pagination.pageSize });
    } else {
      setFilter(f);
    }
  };

  const handleSearch = () => {
    fetchData(filter, pagination);
  };

  const handleReset = () => {
    fetchData({}, { page: 1, pageSize: pagination.pageSize });
  };

  const handlePaginationChange = (
    e: React.ChangeEvent<unknown>,
    value: number
  ) => {
    fetchData(filter, { page: value, pageSize: pagination.pageSize });
  };

  const handleStatisticSelect = (v: {
    [k in (typeof SEARCH_STATUS_KEYS)[number]]?: string;
  }) => {
    const newFilter = {
      ...filter,
      ...SEARCH_STATUS_KEYS.reduce((acc, key) => {
        acc[key] = undefined;
        return acc;
      }, {} as { [k in (typeof SEARCH_STATUS_KEYS)[number]]: undefined }),
      ...v,
    };
    fetchData(newFilter, { page: 1, pageSize: pagination.pageSize });
  };

  const handleStatisticDeselect = () => {
    const newFilter = {
      ...filter,
      ...SEARCH_STATUS_KEYS.reduce((acc, key) => {
        acc[key] = undefined;
        return acc;
      }, {} as { [k in (typeof SEARCH_STATUS_KEYS)[number]]: undefined }),
    };
    fetchData(newFilter, { page: 1, pageSize: pagination.pageSize });
  };

  const handleRowAction = (row: InvestorAmlOverview, key: string) => {
    if (key === "assign") {
      handleAfterAssign(row);
    }
  };

  const handleAfterAssign = (row: InvestorAmlOverview) => {
    fetchData(filter, pagination);
  };

  return (
    <PageWrapper>
      <Box>
        <Header title="AML Review List" subtitle="Review Investor AML Status" />
      </Box>
      <Filter
        value={filter}
        showCustomerFilter={showCustomerFilter}
        subCustomers={subCustomers}
        loading={isLoading}
        onChange={handleFilterChange}
        onSearch={handleSearch}
        onReset={handleReset}
      />
      <Box sx={{ margin: "8px 0" }}>
        <Statistic
          value={statistic}
          loading={isLoading}
          selected={
            filter.status ||
            filter.idvStatus ||
            filter.pepStatus ||
            filter.sancStatus ||
            filter.riskRatingStatus
          }
          onSelect={handleStatisticSelect}
          onDeselect={handleStatisticDeselect}
        />
      </Box>
      <ReviewTable
        loading={isLoading}
        dataSource={dataSource}
        onRowAction={handleRowAction}
      />
      <Stack
        justifyContent="space-between"
        direction="row"
        sx={{ marginTop: "14px" }}
      >
        <Typography>
          Showing {(pagination.page - 1) * PAGE_SIZE + 1}-
          {pagination.page * PAGE_SIZE} of {pagination.total}
        </Typography>
        <Pagination
          disabled={isLoading}
          count={Math.max(Math.ceil(pagination.total / PAGE_SIZE), 1)}
          page={pagination.page}
          onChange={handlePaginationChange}
        />
      </Stack>
    </PageWrapper>
  );
}

function getPayload(
  filter: FilterValue,
  pagination: TPagination
): GetReviewListParams {
  return {
    ...filter,
    limit: String(pagination.pageSize),
    skip: String((pagination.page - 1) * pagination.pageSize),
  };
}

function paramsToFilter(sp: URLSearchParams): FilterValue {
  const statusObject = SEARCH_STATUS_KEYS.reduce((acc, key) => {
    acc[key] = sp.get(key) || "";
    return acc;
  }, {} as { [k in (typeof SEARCH_STATUS_KEYS)[number]]?: string });

  return {
    accountId: sp.get("accountId") || "",
    personId: sp.get("personId") || "",
    onboardingModel: sp.get("onboardingModel") || "",
    customerId: sp.get("customerId") || "",
    ...statusObject
  };
}

function paramsToPagination(sp: URLSearchParams): TPagination {
  return {
    page: sp.get("page") ? Number(sp.get("page")) : 1,
    pageSize: sp.get("pageSize") ? Number(sp.get("pageSize")) : PAGE_SIZE,
  };
}

export default AmlReviewList;
